// eslint-disable-next-line no-restricted-imports
import { ActionType } from "@src/app/store/session";
import { exchangeCognitoTokenForFirebase } from "@src/appV2/Auth/cognito/tokenExchange";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
// eslint-disable-next-line no-restricted-imports
import { getCognitoSingleton } from "@src/lib/cognito/src/lib/cognitoSingleton";
// eslint-disable-next-line no-restricted-imports
import { getFirebaseSingleton } from "@src/lib/firebase/src";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
// eslint-disable-next-line no-restricted-imports
import { useDispatch } from "react-redux";
import { z } from "zod";

import { type ApiResponse, post } from "../../api/api";
import { type IdentifyProvider, IdentifyProviderEnum } from "./types";

const validateOtpRequestSchema = z.object({
  phone: z.string(),
  otp: z.string(),
  isSignup: z.boolean(),
});

export enum ValidateOtpError {
  OTP_EXPIRED = "OtpExpired",
  INVALID_OTP = "InvalidOtp",
  INVALID_USER_FOR_LOGIN = "InvalidUserForLogin",
  INVALID_USER_FOR_SIGNUP = "InvalidUserForSignup",
}

const validateOtpResponseSchema = z.discriminatedUnion("message", [
  z.object({
    message: z.literal("otpIsValid"),
    data: z.object({
      firebaseToken: z.string(),
      agentDeletionRequestCancelled: z.boolean(),
    }),
  }),
  z.object({
    message: z.literal("Invalid OTP"),
    error: z.literal(ValidateOtpError.INVALID_OTP),
  }),
  z.object({
    message: z.literal("Otp Expired"),
    error: z.literal(ValidateOtpError.OTP_EXPIRED),
  }),
  z.object({
    message: z.literal(
      "We could not find your account. Please try again or contact support for further assistance."
    ),
    error: z.enum([
      ValidateOtpError.INVALID_USER_FOR_LOGIN,
      ValidateOtpError.INVALID_USER_FOR_SIGNUP,
    ]),
  }),
]);

export type ValidateOtpRequest = z.infer<typeof validateOtpRequestSchema>;
export type ValidateOtpResponse = z.infer<typeof validateOtpResponseSchema>;
type MutationSchema = ValidateOtpRequest & { identityProvider: IdentifyProvider };

export const VALIDATE_OTP_PATH = "/agentProfile/v2/validateOTP";

export function useValidateOtp(
  options: UseMutationOptions<ApiResponse<ValidateOtpResponse>, AxiosError, ValidateOtpRequest> = {}
): UseMutationResult<ApiResponse<ValidateOtpResponse>, AxiosError, MutationSchema> {
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async (input: MutationSchema) => {
      const { identityProvider, isSignup, otp, phone } = input;

      // Cognito for signup (onboarding)
      if (identityProvider === IdentifyProviderEnum.cognito && isSignup) {
        const cognitoAuth = getCognitoSingleton();

        /**
         * For Cognito signups, we confirm the signup with Cognito, then exchange the Cognito
         * token for a Firebase custom token and establish a Firebase session.
         *
         * The backend creates the Firebase user during token exchange, but the CBH user
         * (with cbhUserId) is created later during the onboarding flow when the user
         * submits their email, name, and address.
         */
        try {
          await cognitoAuth.confirmSignUp(otp);
        } catch (error) {
          // Any error during OTP confirmation is treated as invalid OTP
          logError(APP_V2_APP_EVENTS.VALIDATE_OTP_FAILURE, {
            error,
            metadata: { identityProvider, phone, isSignup },
          });
          return {
            status: 200,
            statusText: "OK",
            data: {
              message: "Invalid OTP",
              error: ValidateOtpError.INVALID_OTP,
            },
          } satisfies ApiResponse<ValidateOtpResponse>;
        }

        // Exchange Cognito token for Firebase token to force Firebase user creation
        const firebaseToken = await exchangeCognitoTokenForFirebase();
        await getFirebaseSingleton().signInWithCustomToken(firebaseToken);

        /**
         * For Cognito signups, populate the Redux session state with the phone number
         * so the onboarding flow has access to it. This mimics what populateSessionAfterLogin
         * does for Firebase users.
         */
        dispatch({
          type: ActionType.LOGGED_IN,
          data: {
            profile: {
              phone: `+1${phone}`,
            },
          },
        });

        return {
          status: 200,
          statusText: "OK",
          data: {
            message: "otpIsValid",
            data: {
              firebaseToken: "",
              agentDeletionRequestCancelled: false,
            },
          },
        } satisfies ApiResponse<ValidateOtpResponse>;
      }

      // Cognito for login (phone OTP)
      if (identityProvider === IdentifyProviderEnum.cognito && !isSignup) {
        const cognitoAuth = getCognitoSingleton();

        /**
         * For Cognito logins, we confirm the phone OTP with Cognito, then exchange the Cognito
         * token for a Firebase custom token and establish a Firebase session for dual auth.
         */
        try {
          await cognitoAuth.confirmPhoneOtp(otp);
        } catch (error) {
          // Any error during OTP confirmation is treated as invalid OTP
          logError(APP_V2_APP_EVENTS.VALIDATE_OTP_FAILURE, {
            error,
            metadata: { identityProvider, phone },
          });
          return {
            status: 200,
            statusText: "OK",
            data: {
              message: "Invalid OTP",
              error: ValidateOtpError.INVALID_OTP,
            },
          } satisfies ApiResponse<ValidateOtpResponse>;
        }

        // Exchange Cognito token for Firebase token (dual auth)
        const firebaseToken = await exchangeCognitoTokenForFirebase();
        await getFirebaseSingleton().signInWithCustomToken(firebaseToken);

        return {
          status: 200,
          statusText: "OK",
          data: {
            message: "otpIsValid",
            data: {
              firebaseToken,
              agentDeletionRequestCancelled: false,
            },
          },
        } satisfies ApiResponse<ValidateOtpResponse>;
      }

      return await post({
        url: VALIDATE_OTP_PATH,
        data: { isSignup, otp, phone },
        requestSchema: validateOtpRequestSchema,
        responseSchema: validateOtpResponseSchema,
      });
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.VALIDATE_OTP_FAILURE,
    },
    ...options,
  });
}
